import React, { useState, useEffect, useRef } from 'react';
import { callClaude } from '../utils/claudeProxy';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, IconButton, Button, Chip,
  TextField, Select, MenuItem, FormControl, InputLabel, Divider,
  Tabs, Tab, CircularProgress, Alert, Tooltip,
  Stepper, Step, StepLabel, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChartIcon from '@mui/icons-material/BarChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatIcon from '@mui/icons-material/Chat';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RateReviewIcon from '@mui/icons-material/RateReview';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { query as fsQuery } from 'firebase/firestore';
import {
  doc, getDoc, addDoc, updateDoc, deleteDoc, collection,
  serverTimestamp, getDocs, where, arrayUnion,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import LagbasLogo from '../components/LagbasLogo';
import LinkedInPreviewCard from '../components/LinkedInPreviewCard';
import { STATUSES, POST_TYPES } from './LinkedInVerkstad';
import { useCompanyLogo } from '../hooks/useQRLogo';

// ── Hjälp: hämta PDF och konvertera till base64 ──────────────────────────

async function urlToBase64(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ── AI-prompt ─────────────────────────────────────────────────────────────

function buildPrompt({ type, typeName, productName, description, level, prevContent }) {
  const baseGuidelines = `
Du är LinkedIn-kommunikationsspecialist för Tikab Strukturmekanik AB.

MARKNADSSPRÅK – OBLIGATORISKA RIKTLINJER:
• Ton: Modern, passionerad, med "glimt i ögat". Undvik torrt, tråkigt, nostalgiskt.
• Identitet: Passion, kompetens, framåtanda, framgång och trygghet (grundat 1977).
• Motto: "Från Insikt till Effekt" – Tikab driver tankar och genomförande, inte bara utför.
• Inkludera alltid ett strategiskt perspektiv när möjligt.
• Värdeord: Passion, Enkelt, Kvalitet
• Tikabs affärsområden: BTM (BuildTech Management), IV (Installationsverksamhet), MEK (Mechanical Design)

LINKEDIN-FORMAT:
• Inledning som fångar uppmärksamhet inom 2 meningar
• Radbrytningar för läsbarhet (blandrader mellan stycken)
• Max ~1200 tecken
• Avsluta med 3–5 relevanta hashtags (t.ex. #tikab #strukturmekanik)
• Använd INTE citattecken runt hela texten
• Returnera ENBART inläggstexten – inga förklaringar, rubriker eller kommentarer
`.trim();

  if (type === 'produkt') {
    if (level === 0) {
      return `${baseGuidelines}

AVSÄNDARE: Produkten/varumärket "${productName}" (ett Tikab-initiativ)
UPPDRAG: Skriv ett LinkedIn-inlägg från ${productName}s perspektiv. Presentera produkten passionerat och professionellt. Gör det intresseväckande för målgruppen.

INNEHÅLLSBESKRIVNING:
${description}`;
    }
    if (level === 1) {
      return `${baseGuidelines}

AVSÄNDARE: Tikab Strukturmekanik AB (bolagets officiella konto)
UPPDRAG: Tikab ska DELA ${productName}s inlägg och lägga till en kortare, stödjande kommentar. Kommentaren sätter produkten i Tikabs kontext och visar stolthet. Ca 300–600 tecken.

ORIGINALINLÄGG FRÅN ${productName}:
${prevContent}

Skriv ENBART Tikabs kommentar/delningstext.`;
    }
    if (level === 2) {
      return `${baseGuidelines}

AVSÄNDARE: En passionerad medarbetare på Tikab (ambassadör)
UPPDRAG: Medarbetaren ska DELA inlägget med en personlig, autentisk kommentar. Tonen är personlig, engagerad och äkta – inte corporate. Ca 200–500 tecken. Kan gärna vara lite personlig.

TIKABS DELNINGSTEXT:
${prevContent}

Skriv ENBART medarbetarens personliga kommentar.`;
    }
  }

  const senderMap = {
    tikab: 'Tikab Strukturmekanik AB (bolagets officiella konto)',
    ambassadör: 'En passionerad medarbetare på Tikab (ambassadör – personlig ton, äkta, engagerad)',
    btm: 'Tikabs affärsområde BTM (BuildTech Management)',
    iv: 'Tikabs affärsområde IV (Installationsverksamhet)',
    mek: 'Tikabs affärsområde MEK (Mechanical Design)',
  };

  return `${baseGuidelines}

AVSÄNDARE: ${senderMap[type] || 'Tikab Strukturmekanik AB'}
UPPDRAG: Skriv ett LinkedIn-inlägg baserat på nedanstående beskrivning.

INNEHÅLLSBESKRIVNING:
${description}`;
}

// ── BEAST-statusar ────────────────────────────────────────────────────────

const BEAST_STATUSES = [
  { id: 'ny_kommentar',  label: 'Ny kommentar',  color: '#fff', bgcolor: '#D63384' },
  { id: 'ska_atgardas',  label: 'Ska åtgärdas',  color: '#fff', bgcolor: '#D32F2F' },
  { id: 'har_atgardats', label: 'Har åtgärdats', color: '#fff', bgcolor: '#388E3C' },
  { id: 'avvisad',       label: 'Avvisad',        color: '#fff', bgcolor: '#616161' },
];

// ── Hjälpfunktioner ───────────────────────────────────────────────────────

const TIER_LABELS = ['Produktinlägg', 'Tikab delar', 'Medarbetare delar'];
const TIER_ICONS = [<BusinessIcon />, <LinkedInIcon />, <PersonIcon />];

function charCount(text = '') { return text.length; }

function userInitials(name = '') {
  return name.split(' ').filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

// ── Huvud-komponent ───────────────────────────────────────────────────────

export default function LinkedInPostEditor() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isNew = !postId || postId === 'new';

  // Post state
  const [post, setPost] = useState({
    type: 'tikab',
    title: '',
    description: '',
    content: '',
    imageUrls: [],        // ersätter imageUrl
    productName: '',
    themeId: '',
    themeLabel: '',
    scheduledDate: '',
    assignedToName: '',
    status: 'idé',
    linkedInUrl: '',
    stats: { visningar: '', reaktioner: '', kommentarer: '', delningar: '', klick: '' },
    tiers: [
      { content: '', imageUrls: [], authorName: '', authorTitle: '' },
      { content: '', imageUrls: [], authorName: 'Tikab Strukturmekanik AB', authorTitle: 'Konsultföretag inom teknik • Solna' },
      { content: '', imageUrls: [], authorName: '', authorTitle: '' },
    ],
    comments: [],    // [{ text, authorName, authorId, isReviewer, createdAt }]
    likes: [],       // [userId, ...]
    createdBy: '',
  });

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [tierTab, setTierTab] = useState(0);
  const [snack, setSnack] = useState('');
  const [imgUploading, setImgUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [marknadDocs, setMarknadDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [sharingVariants, setSharingVariants] = useState([]);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState('');
  const [userProfile, setUserProfile] = useState({});
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newCommentStatus, setNewCommentStatus] = useState('ny_kommentar');
  const [commentSaving, setCommentSaving] = useState(false);
  // Produktlista
  const [products, setProducts] = useState([]);
  const [productDialog, setProductDialog] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [productSaving, setProductSaving] = useState(false);
  // Tema
  const [newThemeLabel, setNewThemeLabel] = useState('');
  const [themeInputOpen, setThemeInputOpen] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);

  const companyLogoUrl = useCompanyLogo();

  const fileInputRef = useRef();
  const tierFileInputRefs = [useRef(), useRef(), useRef()];

  const isProduct = post.type === 'produkt';
  const isReviewer = ['superadmin', 'admin', 'marknadsansvarig', 'ao_ansvarig'].includes(userRole)
    || userProfile?.canManageMarketing === true
    || userProfile?.isAdmin === true;
  const canDelete = currentUser && (
    post.createdBy === currentUser.uid ||
    ['superadmin', 'admin'].includes(userRole)
  );
  const isLiked = currentUser ? post.likes.includes(currentUser.uid) : false;

  // Ladda existerande inlägg
  useEffect(() => {
    if (isNew) return;
    (async () => {
      const snap = await getDoc(doc(db, 'linkedinPosts', postId));
      if (snap.exists()) {
        const data = snap.data();
        // Bakåtkompatibilitet: migrera imageUrl → imageUrls
        const imageUrls = data.imageUrls?.length
          ? data.imageUrls
          : data.imageUrl
            ? [data.imageUrl]
            : [];
        // Tiers bakåtkompatibilitet
        const tiers = (data.tiers || []).map(t => ({
          ...t,
          imageUrls: t.imageUrls?.length ? t.imageUrls : t.imageUrl ? [t.imageUrl] : [],
        }));
        setPost(prev => ({
          ...prev,
          ...data,
          imageUrls,
          tiers: tiers.length ? tiers : prev.tiers,
          themeId: data.themeId || '',
          themeLabel: data.themeLabel || '',
          scheduledDate: data.scheduledDate
            ? (data.scheduledDate.toDate ? data.scheduledDate.toDate() : new Date(data.scheduledDate))
                .toISOString().slice(0, 10)
            : '',
          stats: data.stats || { visningar: '', reaktioner: '', kommentarer: '', delningar: '', klick: '' },
          comments: data.comments || [],
          likes: data.likes || [],
          createdBy: data.createdBy || '',
        }));
        if (data.sharingVariants?.length) {
          setSharingVariants(data.sharingVariants);
        }
      }
      setLoading(false);
    })();
  }, [postId, isNew]);

  // Ladda inloggad användares roll och profil
  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then(snap => {
      const d = snap.data() || {};
      setUserRole(d.role || '');
      setUserName(`${d.firstName || ''} ${d.lastName || ''} ${d.name || ''}`.trim().split(/\s+/).slice(0,2).join(' '));
      setUserProfile(d);
    });
  }, [currentUser]);

  // Ladda användare + avdelningar för Ansvarig-dropdown
  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'departments')),
    ]).then(([usersSnap, deptsSnap]) => {
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setDepartments(deptsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(() => {});
  }, []);

  // Ladda produkter
  useEffect(() => {
    getDocs(collection(db, 'products'))
      .then(snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, []);

  // Ladda styrande marknadsdokument
  useEffect(() => {
    setDocsLoading(true);
    getDocs(fsQuery(collection(db, 'documentLibrary'), where('category', '==', 'Marknad')))
      .then(snap => setMarknadDocs(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
      .finally(() => setDocsLoading(false));
  }, []);

  function setField(field, value) {
    setPost(prev => ({ ...prev, [field]: value }));
  }

  function setTierField(idx, field, value) {
    setPost(prev => {
      const tiers = [...prev.tiers];
      tiers[idx] = { ...tiers[idx], [field]: value };
      return { ...prev, tiers };
    });
  }

  // ── AI-generering ──

  async function generateAI(tierLevel = null) {
    const description = post.description;
    if (!description.trim()) {
      setAiError('Fyll i beskrivningen av vad du vill säga först.');
      return;
    }
    if (isProduct && tierLevel === 0 && !post.productName.trim()) {
      setAiError('Välj en produkt (eller skapa en ny med +).');
      return;
    }
    setAiLoading(true);
    setAiError('');
    const prevContent = isProduct && tierLevel > 0 ? post.tiers[tierLevel - 1]?.content : '';
    const prompt = buildPrompt({
      type: post.type,
      typeName: POST_TYPES[post.type]?.label || post.type,
      productName: post.productName,
      description,
      level: tierLevel,
      prevContent,
    });

    try {
      const contentBlocks = [];
      for (const mdoc of marknadDocs) {
        if (mdoc.currentFileUrl) {
          try {
            const b64 = await urlToBase64(mdoc.currentFileUrl);
            contentBlocks.push({
              type: 'document',
              source: { type: 'base64', media_type: 'application/pdf', data: b64 },
              title: mdoc.name,
              context: 'Styrande marknadsdokument – följ dessa riktlinjer.',
            });
          } catch { /* hoppa över */ }
        }
      }
      contentBlocks.push({ type: 'text', text: prompt });

      const data = await callClaude({
        model: 'claude-opus-5',
        max_tokens: 1024,
        messages: [{ role: 'user', content: contentBlocks }],
      });
      const text = data.content?.[0]?.text || '';
      if (isProduct && tierLevel !== null) {
        setTierField(tierLevel, 'content', text.trim());
      } else {
        setField('content', text.trim());
      }
    } catch {
      setAiError('Något gick fel med AI-genereringen. Försök igen.');
    } finally {
      setAiLoading(false);
    }
  }

  // ── Delningsförslag ──

  async function generateSharingVariants() {
    const mainContent = post.content;
    if (!mainContent?.trim()) return;
    setSharingLoading(true);
    setSharingVariants([]);
    const senderLabel = POST_TYPES[post.type]?.label || 'Tikab';
    const prompt = `Du är LinkedIn-kommunikationsspecialist för Tikab Strukturmekanik AB.

Nedan finns ett LinkedIn-inlägg från ${senderLabel}. En medarbetare vill dela inlägget vidare med sin egen kommentar.

Generera EXAKT 3 varianter av delningskommentarer, separerade av "---VARIANT---".
Varianterna ska vara:
1. PERSONLIG – Varm, äkta, engagerad. Som om personen verkligen brinner för det. Ca 150–300 tecken.
2. PROFESSIONELL – Mer formell men ändå passionerad. Lyfter det strategiska värdet. Ca 150–300 tecken.
3. KORT & RAPP – Slagkraftig, under 100 tecken. Gärna med en känsla eller emoji.

Skriv ENBART de tre kommentarstexterna separerade av "---VARIANT---". Inga rubriker, inga förklaringar.

ORIGINALINLÄGG:
${mainContent}`;

    try {
      const contentBlocks = [];
      for (const mdoc of marknadDocs) {
        if (mdoc.currentFileUrl) {
          try {
            const b64 = await urlToBase64(mdoc.currentFileUrl);
            contentBlocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: b64 } });
          } catch { /* hoppa över */ }
        }
      }
      contentBlocks.push({ type: 'text', text: prompt });
      const data = await callClaude({ model: 'claude-opus-5', max_tokens: 800, messages: [{ role: 'user', content: contentBlocks }] });
      const text = data.content?.[0]?.text || '';
      const variants = text.split('---VARIANT---').map(v => v.trim()).filter(Boolean);
      const labels = ['Personlig', 'Professionell', 'Kort & rapp'];
      const parsed = variants.slice(0, 3).map((v, i) => ({ label: labels[i] || `Variant ${i + 1}`, text: v }));
      setSharingVariants(parsed);
      if (!isNew && postId) {
        updateDoc(doc(db, 'linkedinPosts', postId), { sharingVariants: parsed }).catch(() => {});
      }
    } catch {
      setSharingVariants([]);
    } finally {
      setSharingLoading(false);
    }
  }

  // ── Bilduppladdning (stöd för flera) ──

  async function handleImageUpload(file, tierLevel = null) {
    if (!file) return;
    setImgUploading(true);
    const path = `linkedin/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed', null, err => {
      console.error(err);
      setImgUploading(false);
    }, async () => {
      const url = await getDownloadURL(task.snapshot.ref);
      if (tierLevel !== null) {
        setPost(prev => {
          const tiers = [...prev.tiers];
          tiers[tierLevel] = { ...tiers[tierLevel], imageUrls: [...(tiers[tierLevel].imageUrls || []), url] };
          return { ...prev, tiers };
        });
      } else {
        setPost(prev => ({ ...prev, imageUrls: [...prev.imageUrls, url] }));
      }
      setImgUploading(false);
    });
  }

  function removeImage(index, tierLevel = null) {
    if (tierLevel !== null) {
      setPost(prev => {
        const tiers = [...prev.tiers];
        const imgs = [...(tiers[tierLevel].imageUrls || [])];
        imgs.splice(index, 1);
        tiers[tierLevel] = { ...tiers[tierLevel], imageUrls: imgs };
        return { ...prev, tiers };
      });
    } else {
      setPost(prev => {
        const imgs = [...prev.imageUrls];
        imgs.splice(index, 1);
        return { ...prev, imageUrls: imgs };
      });
    }
  }

  // ── Like (intern tumme upp) ──

  async function handleLike() {
    if (!currentUser || isNew) return;
    const uid = currentUser.uid;
    const alreadyLiked = post.likes.includes(uid);
    const newLikes = alreadyLiked
      ? post.likes.filter(id => id !== uid)
      : [...post.likes, uid];
    setPost(prev => ({ ...prev, likes: newLikes }));
    try {
      await updateDoc(doc(db, 'linkedinPosts', postId), { likes: newLikes });
    } catch { /* roll back */
      setPost(prev => ({ ...prev, likes: post.likes }));
    }
  }

  // ── Kommentar (granskningschat) ──

  async function handleDeleteComment(index) {
    try {
      const updated = post.comments.filter((_, i) => i !== index);
      await updateDoc(doc(db, 'linkedinPosts', postId), { comments: updated });
      setPost(prev => ({ ...prev, comments: updated }));
    } catch {
      setSnack('Kunde inte radera kommentaren.');
    }
  }

  async function handleAddComment() {
    const text = newComment.trim();
    if (!text || !postId || isNew) return;
    setCommentSaving(true);
    const comment = {
      text,
      authorName: userName || 'Okänd',
      authorId: currentUser?.uid || '',
      isReviewer,
      beastStatus: newCommentStatus || null,
      createdAt: new Date().toISOString(),
    };
    try {
      await updateDoc(doc(db, 'linkedinPosts', postId), { comments: arrayUnion(comment) });
      setPost(prev => ({ ...prev, comments: [...prev.comments, comment] }));
      setNewComment('');
      setNewCommentStatus('ny_kommentar');
    } catch (e) {
      setSnack('Kunde inte spara kommentaren.');
    } finally {
      setCommentSaving(false);
    }
  }

  // ── Skicka för granskning ──

  async function handleSendForReview() {
    await handleSave('granskning', { reviewRequestedBy: userName });
    try {
      await addDoc(collection(db, 'notifications'), {
        type: 'linkedin_review',
        postId: postId || 'new',
        postTitle: post.title || '(Namnlöst inlägg)',
        fromName: userName,
        message: `${userName || 'Någon'} har skickat ett LinkedIn-inlägg för granskning: "${post.title || 'Namnlöst'}"`,
        targetRoles: ['superadmin', 'admin', 'marknadsansvarig'],
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch { /* notifiering är inte kritisk */ }
    setSnack('Inlägget har skickats för granskning!');
  }

  async function handleApprove() {
    await handleSave('klar');
    try {
      await updateDoc(doc(db, 'linkedinPosts', postId), {
        approvedBy: userName,
        approvedAt: serverTimestamp(),
        reviewComment: '',
      });
    } catch { /* */ }
    setSnack('Inlägget är godkänt!');
  }

  async function handleRequestChanges() {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'linkedinPosts', postId), {
        status: 'utkast',
        reviewComment: reviewComment.trim(),
        updatedAt: serverTimestamp(),
      });
      setField('status', 'utkast');
      await addDoc(collection(db, 'notifications'), {
        type: 'linkedin_changes_requested',
        postId,
        postTitle: post.title || '(Namnlöst inlägg)',
        fromName: userName,
        message: `${userName} har begärt ändringar på "${post.title || 'Namnlöst'}": ${reviewComment}`,
        targetUserId: post.createdBy || '',
        read: false,
        createdAt: serverTimestamp(),
      });
      setReviewDialog(false);
      setReviewComment('');
      setSnack('Kommentar skickad, inlägget är tillbaka i Utkast.');
    } catch (e) {
      setSnack('Fel: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Radera inlägg ──

  async function handleDelete() {
    if (!postId || isNew) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'linkedinPosts', postId));
      navigate('/marketing/linkedin');
    } catch (e) {
      setSnack('Kunde inte radera inlägget: ' + e.message);
      setDeleting(false);
    }
  }

  // ── Skapa ny produkt ──

  async function handleAddProduct() {
    const name = newProductName.trim();
    if (!name) return;
    setProductSaving(true);
    try {
      const docRef = await addDoc(collection(db, 'products'), {
        name,
        createdAt: serverTimestamp(),
      });
      const newProduct = { id: docRef.id, name };
      setProducts(prev => [...prev, newProduct]);
      setField('productName', name);
      setNewProductName('');
      setProductDialog(false);
      setSnack(`Produkten "${name}" skapades!`);
    } catch (e) {
      setSnack('Kunde inte skapa produkten: ' + e.message);
    } finally {
      setProductSaving(false);
    }
  }

  // ── Lägg till tema på produkt ──

  async function handleAddTheme() {
    const label = newThemeLabel.trim();
    if (!label) return;
    const prod = products.find(p => p.name === post.productName);
    if (!prod) return;
    setThemeSaving(true);
    try {
      const newTheme = { id: Date.now().toString(), label };
      await updateDoc(doc(db, 'products', prod.id), {
        themes: arrayUnion(newTheme),
      });
      setProducts(prev => prev.map(p =>
        p.id === prod.id ? { ...p, themes: [...(p.themes || []), newTheme] } : p
      ));
      setField('themeId', newTheme.id);
      setField('themeLabel', newTheme.label);
      setNewThemeLabel('');
      setThemeInputOpen(false);
    } catch (e) {
      setSnack('Kunde inte spara tema: ' + e.message);
    } finally {
      setThemeSaving(false);
    }
  }

  // ── Spara ──

  async function handleSave(newStatus, extraPayload = {}) {
    setSaving(true);
    try {
      const scheduledDate = post.scheduledDate ? new Date(post.scheduledDate) : null;
      const payload = {
        type: post.type,
        title: post.title,
        description: post.description,
        content: post.content,
        imageUrls: post.imageUrls || [],
        productName: post.productName || '',
        themeId: post.themeId || '',
        themeLabel: post.themeLabel || '',
        scheduledDate: scheduledDate || null,
        assignedToName: post.assignedToName || '',
        status: newStatus || post.status,
        linkedInUrl: post.linkedInUrl || '',
        stats: post.stats || {},
        tiers: isProduct ? post.tiers.map(t => ({ ...t, imageUrls: t.imageUrls || [] })) : [],
        comments: post.comments || [],
        likes: post.likes || [],
        updatedAt: serverTimestamp(),
        ...extraPayload,
      };

      if (isNew) {
        payload.createdAt = serverTimestamp();
        payload.createdBy = currentUser?.uid || '';
        const r = await addDoc(collection(db, 'linkedinPosts'), payload);
        navigate(`/marketing/linkedin/${r.id}`, { replace: true });
      } else {
        await updateDoc(doc(db, 'linkedinPosts', postId), payload);
      }

      if (newStatus) setField('status', newStatus);
      setSnack('Inlägget sparades!');
    } catch (e) {
      setSnack('Fel vid sparning: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Render ──

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentStatus = STATUSES.find(s => s.id === post.status);
  const typeInfo = POST_TYPES[post.type] || POST_TYPES.tikab;
  // Ansvarig-alternativ: marknadsansvarig + marknadskonsult + AO-medarbetare (baserat på typ)
  const DEPT_POST_TYPES = ['btm', 'iv', 'mek'];
  const matchedDept = departments.find(d =>
    (d.offerCode || d.name || '').toLowerCase() === (post.type || '').toLowerCase()
  );
  const userOptions = users
    .filter(u => {
      const role = u.role || '';
      if (role === 'marknadsansvarig' || role === 'marknadskonsult') return true;
      if (DEPT_POST_TYPES.includes(post.type) && matchedDept) {
        return u.departmentId === matchedDept.id;
      }
      return !DEPT_POST_TYPES.includes(post.type); // tikab/ambassadör/produkt → alla
    })
    .map(u => `${u.firstName || ''} ${u.lastName || ''}`.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'sv'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2, position: 'sticky', top: 0, zIndex: 100,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/marketing/linkedin')}>
          <ArrowBackIcon />
        </IconButton>
        <LinkedInIcon sx={{ color: '#0a66c2' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {isNew ? 'Nytt inlägg' : post.title || 'Redigera inlägg'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentStatus && (
              <Chip
                label={currentStatus.label}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', height: 18, fontSize: '0.65rem' }}
              />
            )}
            <Chip
              label={typeInfo.label}
              size="small"
              sx={{ bgcolor: typeInfo.bg, color: typeInfo.color, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
            />
          </Box>
        </Box>

        {/* Status-knappar */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isReviewer && ['idé', 'utkast'].includes(post.status) && !isNew && (
            <Button
              size="small" variant="outlined"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <RateReviewIcon />}
              onClick={handleSendForReview}
              disabled={saving || !post.content}
              sx={{ color: '#ffb74d', borderColor: '#ffb74d', fontSize: '0.72rem', whiteSpace: 'nowrap' }}
            >
              Skicka för granskning
            </Button>
          )}

          {isReviewer && post.status === 'granskning' && !isNew && (
            <>
              <Button
                size="small" variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                onClick={handleApprove}
                disabled={saving}
                sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: '0.72rem' }}
              >
                Godkänn
              </Button>
              <Button
                size="small" variant="outlined"
                startIcon={<GppMaybeIcon />}
                onClick={() => setReviewDialog(true)}
                disabled={saving}
                sx={{ color: '#ffb74d', borderColor: '#ffb74d', fontSize: '0.72rem', whiteSpace: 'nowrap' }}
              >
                Begär ändringar
              </Button>
            </>
          )}

          {canDelete && !isNew && (
            <Tooltip title="Radera inlägg">
              <IconButton
                size="small"
                onClick={() => setDeleteDialog(true)}
                sx={{ color: 'rgba(255,100,100,0.8)', '&:hover': { color: '#ff5252' } }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )}

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={() => handleSave()}
            disabled={saving}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}
          >
            Spara
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

          {/* ── Vänster: Metadata + innehåll ── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* Granskningskommentar-banner */}
            {post.reviewComment && post.status === 'utkast' && (
              <Alert severity="warning" icon={<GppMaybeIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Ändringar begärda av marknadsansvarig
                </Typography>
                <Typography variant="body2">{post.reviewComment}</Typography>
              </Alert>
            )}

            {/* Godkänt-banner */}
            {post.status === 'klar' && post.approvedBy && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                Godkänt av <strong>{post.approvedBy}</strong>
              </Alert>
            )}

            {/* Metadata */}
            <Paper sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                Inläggsinformation
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Titel */}
                <TextField
                  label="Intern titel"
                  value={post.title}
                  onChange={e => setField('title', e.target.value)}
                  size="small"
                  fullWidth
                  helperText="Visas bara internt"
                />

                {/* Typ */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Typ</InputLabel>
                  <Select value={post.type} label="Typ" onChange={e => setField('type', e.target.value)}>
                    {Object.entries(POST_TYPES).map(([k, v]) => (
                      <MenuItem key={k} value={k}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: v.color }} />
                          {v.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Produkt – välj från lista + skapa ny */}
                {isProduct && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', minWidth: 240 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Produkt</InputLabel>
                      <Select
                        value={post.productName}
                        label="Produkt"
                        onChange={e => setField('productName', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Välj produkt…</em></MenuItem>
                        {products.map(p => (
                          <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Skapa ny produkt">
                      <IconButton size="small" onClick={() => setProductDialog(true)} sx={{ mb: 0.5, color: '#FF6B00' }}>
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Tema – visas när produkt är vald */}
                {isProduct && post.productName && (() => {
                  const prod = products.find(p => p.name === post.productName);
                  const themes = prod?.themes || [];
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, minWidth: 240 }}>
                      <Typography variant="caption" sx={{ color: '#555', mr: 0.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        Tema:
                      </Typography>
                      {themes.map(th => (
                        <Chip
                          key={th.id}
                          label={th.label}
                          size="small"
                          onClick={() => {
                            if (post.themeId === th.id) {
                              setField('themeId', '');
                              setField('themeLabel', '');
                            } else {
                              setField('themeId', th.id);
                              setField('themeLabel', th.label);
                            }
                          }}
                          sx={{
                            bgcolor: post.themeId === th.id ? '#00838F' : '#E0F7FA',
                            color: post.themeId === th.id ? '#fff' : '#00838F',
                            fontWeight: 600, fontSize: '0.72rem', height: 22,
                            cursor: 'pointer',
                          }}
                        />
                      ))}
                      {themeInputOpen ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            value={newThemeLabel}
                            onChange={e => setNewThemeLabel(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleAddTheme(); if (e.key === 'Escape') { setThemeInputOpen(false); setNewThemeLabel(''); } }}
                            placeholder="Nytt tema…"
                            autoFocus
                            sx={{ width: 140, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.78rem' } }}
                          />
                          <IconButton size="small" onClick={handleAddTheme} disabled={!newThemeLabel.trim() || themeSaving} sx={{ color: '#00838F' }}>
                            <AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => { setThemeInputOpen(false); setNewThemeLabel(''); }} sx={{ color: '#999' }}>
                            <CloseIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Tooltip title="Lägg till tema">
                          <IconButton size="small" onClick={() => setThemeInputOpen(true)} sx={{ color: '#00838F', p: 0.25 }}>
                            <AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })()}

                {/* Datum */}
                <TextField
                  label="Schemalagd publicering"
                  type="date"
                  value={post.scheduledDate}
                  onChange={e => setField('scheduledDate', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />

                {/* Ansvarig – Autocomplete (filtrerat på AO + marknadsroller) */}
                <Autocomplete
                  size="small"
                  options={userOptions}
                  value={post.assignedToName || null}
                  onChange={(_, v) => setField('assignedToName', v || '')}
                  sx={{ minWidth: 200 }}
                  renderInput={params => <TextField {...params} label="Ansvarig" />}
                  renderOption={(props, option) => {
                    const u = users.find(u => `${u.firstName || ''} ${u.lastName || ''}`.trim() === option);
                    return (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: '#FF6B00' }}>
                          {userInitials(option)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{option}</Typography>
                          {u?.role && (
                            <Typography variant="caption" color="text.secondary">{u.role}</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                />

                {/* Status */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={post.status} label="Status" onChange={e => setField('status', e.target.value)}>
                    {STATUSES.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            {/* ── Enkel inlägg ── */}
            {!isProduct && (
              <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  Innehåll
                </Typography>

                <TextField
                  label="Vad vill du förmedla? (råmaterial för AI)"
                  value={post.description}
                  onChange={e => setField('description', e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder="Beskriv vad du vill säga, t.ex. 'Vi har precis avslutat ett spännande projekt där vi hjälpte kund X med Y...' AI omvandlar detta till ett passionerat LinkedIn-inlägg i Tikabs ton."
                  sx={{ mb: 1.5 }}
                />

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={() => generateAI()}
                    disabled={aiLoading}
                    sx={{ bgcolor: '#0a66c2', '&:hover': { bgcolor: '#084a96' } }}
                  >
                    Generera med AI
                  </Button>
                  {post.content && (
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => generateAI()} disabled={aiLoading} size="small">
                      Regenerera
                    </Button>
                  )}
                </Box>

                {aiError && <Alert severity="error" sx={{ mb: 1.5 }}>{aiError}</Alert>}

                <TextField
                  label="LinkedIn-inlägg"
                  value={post.content}
                  onChange={e => setField('content', e.target.value)}
                  multiline
                  minRows={6}
                  fullWidth
                  placeholder="Klicka 'Generera med AI' eller skriv direkt här…"
                  helperText={`${charCount(post.content)} tecken`}
                />

                {/* Bilduppladdning – flera bilder */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Bilder ({post.imageUrls.length}/9) – ordningen avgör layout i preview
                  </Typography>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={e => handleImageUpload(e.target.files[0])}
                  />
                  {/* Befintliga bilder */}
                  {post.imageUrls.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      {post.imageUrls.map((url, i) => (
                        <Box key={i} sx={{ position: 'relative', width: 80, height: 60 }}>
                          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                          <IconButton
                            size="small"
                            onClick={() => removeImage(i)}
                            sx={{
                              position: 'absolute', top: -6, right: -6,
                              bgcolor: 'rgba(0,0,0,0.7)', color: 'white',
                              width: 18, height: 18, p: 0,
                              '&:hover': { bgcolor: 'rgba(200,0,0,0.8)' },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: '0.7rem' }} />
                          </IconButton>
                          <Typography sx={{
                            position: 'absolute', bottom: 2, left: 2,
                            fontSize: '0.55rem', color: '#fff', bgcolor: 'rgba(0,0,0,0.5)',
                            px: 0.4, borderRadius: 0.5,
                          }}>
                            {i + 1}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {post.imageUrls.length < 9 && (
                    <Button
                      variant="outlined"
                      startIcon={imgUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                      onClick={() => fileInputRef.current.click()}
                      disabled={imgUploading}
                      size="small"
                    >
                      {post.imageUrls.length === 0 ? 'Lägg till bild/film' : 'Lägg till fler bilder'}
                    </Button>
                  )}
                </Box>
              </Paper>
            )}

            {/* ── Delningsförslag ── */}
            {!isProduct && post.content && (
              <Paper sx={{ p: 2.5, mt: 2.5, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ShareOutlinedIcon sx={{ color: '#0a66c2', fontSize: '1.1rem' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Dela vidare – förslag på kommentarer
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Hur kan en medarbetare dela inlägget med sin personliga kommentar?
                    </Typography>
                  </Box>
                  <Button
                    variant="contained" size="small"
                    startIcon={sharingLoading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={generateSharingVariants}
                    disabled={sharingLoading}
                    sx={{ bgcolor: '#0a66c2', '&:hover': { bgcolor: '#084a96' }, whiteSpace: 'nowrap' }}
                  >
                    {sharingVariants.length ? 'Generera nya' : 'Generera förslag'}
                  </Button>
                </Box>
                {sharingVariants.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                    {sharingVariants.map((v, i) => (
                      <Box key={i} sx={{
                        p: 1.5, borderRadius: 1.5,
                        bgcolor: i === 0 ? '#FFF3E0' : i === 1 ? '#E3F2FD' : '#F3F3F3',
                        border: `1px solid ${i === 0 ? '#FFB74D' : i === 1 ? '#90CAF9' : '#e0e0e0'}`,
                        position: 'relative',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.75 }}>
                          <Chip label={v.label} size="small" sx={{
                            fontSize: '0.65rem', height: 18, fontWeight: 700,
                            bgcolor: i === 0 ? '#FF6B00' : i === 1 ? '#1565C0' : '#555', color: 'white',
                          }} />
                          <Typography variant="caption" sx={{ color: '#888', ml: 'auto' }}>{v.text.length} tecken</Typography>
                          <Tooltip title="Kopiera">
                            <IconButton size="small" onClick={() => { navigator.clipboard.writeText(v.text); setSnack('Kopierat!'); }} sx={{ p: 0.4 }}>
                              <ContentCopyIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#1a1a1a' }}>
                          {v.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* ── Produktflöde (3 nivåer) ── */}
            {isProduct && (
              <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    3-nivå delningsflöde
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Börja med att beskriva vad du vill kommunicera. Generera sedan inlägg nivå för nivå.
                  </Typography>
                  <TextField
                    label="Vad handlar det om? (råmaterial för alla nivåer)"
                    value={post.description}
                    onChange={e => setField('description', e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    placeholder="T.ex. 'FLATBOX är vår nya produkt för X, den löser problemet Y och passar kund Z...'"
                    sx={{ mb: 2 }}
                  />
                </Box>

                <Stepper activeStep={tierTab} alternativeLabel sx={{ px: 2, pb: 1 }}>
                  {TIER_LABELS.map((label, i) => (
                    <Step key={label} completed={!!post.tiers[i]?.content}>
                      <StepLabel
                        onClick={() => setTierTab(i)}
                        sx={{ cursor: 'pointer' }}
                        StepIconComponent={() => (
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            bgcolor: i === tierTab ? '#0a66c2' : post.tiers[i]?.content ? '#2E7D32' : '#e0e0e0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontSize: '0.8rem',
                          }}>
                            {i + 1}
                          </Box>
                        )}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Divider />

                {TIER_LABELS.map((label, i) => (
                  <Box key={i} sx={{ display: tierTab === i ? 'block' : 'none', p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      {TIER_ICONS[i]}
                      <Typography fontWeight={600}>{label}</Typography>
                      {i > 0 && !post.tiers[i - 1]?.content && (
                        <Alert severity="info" sx={{ py: 0, flex: 1, fontSize: '0.75rem' }}>
                          Generera nivå {i} först
                        </Alert>
                      )}
                    </Box>

                    {(i === 0 || i === 2) && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                        <TextField
                          label="Avsändarnamn"
                          value={post.tiers[i]?.authorName || ''}
                          onChange={e => setTierField(i, 'authorName', e.target.value)}
                          size="small" sx={{ flex: 1 }}
                          placeholder={i === 0 ? post.productName || 'FLATBOX' : 'Förnamn Efternamn'}
                        />
                        <TextField
                          label="Titel / beskrivning"
                          value={post.tiers[i]?.authorTitle || ''}
                          onChange={e => setTierField(i, 'authorTitle', e.target.value)}
                          size="small" sx={{ flex: 2 }}
                          placeholder={i === 0 ? 'Innovativt system för X • tikab.com' : 'Konstruktionsingenjör på Tikab'}
                        />
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                      <Button
                        variant="contained"
                        startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                        onClick={() => generateAI(i)}
                        disabled={aiLoading || (i > 0 && !post.tiers[i - 1]?.content)}
                        sx={{ bgcolor: '#0a66c2', '&:hover': { bgcolor: '#084a96' } }}
                      >
                        Generera nivå {i + 1}
                      </Button>
                      {post.tiers[i]?.content && (
                        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => generateAI(i)} disabled={aiLoading}>
                          Regenerera
                        </Button>
                      )}
                    </Box>

                    {aiError && <Alert severity="error" sx={{ mb: 1.5 }}>{aiError}</Alert>}

                    <TextField
                      label={`Inläggstext – ${label}`}
                      value={post.tiers[i]?.content || ''}
                      onChange={e => setTierField(i, 'content', e.target.value)}
                      multiline minRows={5} fullWidth
                      helperText={`${charCount(post.tiers[i]?.content)} tecken`}
                    />

                    {/* Bilder per nivå */}
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Bilder ({(post.tiers[i]?.imageUrls || []).length}/9)
                      </Typography>
                      <input
                        type="file" accept="image/*"
                        ref={tierFileInputRefs[i]}
                        style={{ display: 'none' }}
                        onChange={e => handleImageUpload(e.target.files[0], i)}
                      />
                      {(post.tiers[i]?.imageUrls || []).length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          {(post.tiers[i]?.imageUrls || []).map((url, j) => (
                            <Box key={j} sx={{ position: 'relative', width: 80, height: 60 }}>
                              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                              <IconButton
                                size="small" onClick={() => removeImage(j, i)}
                                sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', width: 18, height: 18, p: 0, '&:hover': { bgcolor: 'rgba(200,0,0,0.8)' } }}
                              >
                                <CloseIcon sx={{ fontSize: '0.7rem' }} />
                              </IconButton>
                              <Typography sx={{ position: 'absolute', bottom: 2, left: 2, fontSize: '0.55rem', color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', px: 0.4, borderRadius: 0.5 }}>
                                {j + 1}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {(post.tiers[i]?.imageUrls || []).length < 9 && (
                        <Button
                          variant="outlined"
                          startIcon={imgUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                          onClick={() => tierFileInputRefs[i].current.click()}
                          disabled={imgUploading} size="small"
                        >
                          Lägg till bild
                        </Button>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      {i > 0 && (
                        <Button size="small" onClick={() => setTierTab(i - 1)}>← {TIER_LABELS[i - 1]}</Button>
                      )}
                      {i < 2 && (
                        <Button
                          size="small" variant="contained"
                          onClick={() => setTierTab(i + 1)}
                          disabled={!post.tiers[i]?.content}
                          sx={{ ml: 'auto', bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}
                        >
                          {TIER_LABELS[i + 1]} →
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}

            {/* ── Kommentarschat ── */}
            {!isNew && (
              <Paper sx={{ p: 2.5, mt: 2.5, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                  <ChatIcon sx={{ color: '#FF6B00', fontSize: '1.1rem' }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Granskningskommentarer
                  </Typography>
                  {post.comments.length > 0 && (
                    <Chip label={post.comments.length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>

                {post.comments.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                    Inga kommentarer ännu. Granskare och skaparen kan diskutera inlägget här.
                  </Typography>
                )}

                {/* Kommentarstråd */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2, maxHeight: 320, overflowY: 'auto' }}>
                  {post.comments.map((c, i) => {
                    const beast = c.beastStatus ? BEAST_STATUSES.find(b => b.id === c.beastStatus) : null;
                    return (
                      <Box key={i} sx={{
                        p: 1.25, borderRadius: 1.5,
                        bgcolor: beast ? beast.bgcolor + '18' : c.isReviewer ? '#FFF8E1' : '#F5F5F5',
                        border: `1px solid ${beast ? beast.bgcolor + '55' : c.isReviewer ? '#FFE082' : '#e0e0e0'}`,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                          <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem', bgcolor: c.isReviewer ? '#FF6B00' : '#757575' }}>
                            {userInitials(c.authorName)}
                          </Avatar>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{c.authorName}</Typography>
                          {c.isReviewer && (
                            <Chip label="Granskare" size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#FF6B00', color: '#fff' }} />
                          )}
                          {beast && (
                            <Chip
                              label={beast.label}
                              size="small"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: beast.bgcolor, color: beast.color }}
                            />
                          )}
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', ml: 'auto' }}>
                            {c.createdAt ? new Date(c.createdAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                          </Typography>
                          {(c.authorId === currentUser?.uid || ['superadmin', 'admin'].includes(userRole)) && (
                            <Tooltip title="Radera kommentar">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteComment(i)}
                                sx={{ p: 0.25, color: 'rgba(0,0,0,0.25)', '&:hover': { color: 'error.main' } }}
                              >
                                <CloseIcon sx={{ fontSize: '0.75rem' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                          {c.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                {/* Ny kommentar */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                      placeholder="Skriv en kommentar… (Enter för att skicka)"
                      multiline maxRows={4}
                      size="small" fullWidth
                    />
                    {/* BEAST-statusväljare */}
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                      {BEAST_STATUSES.map(b => (
                        <Chip
                          key={b.id}
                          label={b.label}
                          size="small"
                          onClick={() => setNewCommentStatus(newCommentStatus === b.id ? '' : b.id)}
                          sx={{
                            height: 20, fontSize: '0.62rem', fontWeight: 600,
                            cursor: 'pointer',
                            bgcolor: newCommentStatus === b.id ? b.bgcolor : 'transparent',
                            color: newCommentStatus === b.id ? b.color : b.bgcolor,
                            border: `1.5px solid ${b.bgcolor}`,
                            '&:hover': { bgcolor: b.bgcolor + '33' },
                            transition: 'all 0.15s',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Button
                    variant="contained" size="small"
                    startIcon={commentSaving ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || commentSaving}
                    sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, minWidth: 90, alignSelf: 'flex-start', mt: 0.25 }}
                  >
                    Skicka
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>

          {/* ── Höger: Preview + statistik ── */}
          <Box sx={{ width: 420, flexShrink: 0, position: 'sticky', top: 80 }}>
            {/* Styrande dokument */}
            <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <MenuBookIcon sx={{ fontSize: '1rem', color: '#FF6B00' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                  Styrande dokument
                </Typography>
                <Tooltip title="Dokument taggade 'Marknad' i dokumentbiblioteket. AI:n läser dessa när den genererar inlägg.">
                  <Typography variant="caption" sx={{ color: '#888', cursor: 'help', borderBottom: '1px dashed #ccc' }}>
                    Vad är detta?
                  </Typography>
                </Tooltip>
              </Box>
              {docsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={12} />
                  <Typography variant="caption" color="text.secondary">Laddar…</Typography>
                </Box>
              ) : marknadDocs.length === 0 ? (
                <Alert severity="warning" sx={{ py: 0.25, fontSize: '0.75rem' }}>
                  Inga dokument hittade. Ladda upp Marknadsspråk och Marknadsplan i{' '}
                  <Box component="span" onClick={() => window.open('/document-library', '_blank')} sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    dokumentbiblioteket
                  </Box>
                  {' '}med kategorin <strong>Marknad</strong>.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {marknadDocs.map(d => (
                    <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: '0.85rem', color: '#2E7D32' }} />
                      <Typography variant="caption" sx={{ color: '#333', flex: 1 }}>{d.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.65rem' }}>v{d.currentVersion}</Typography>
                    </Box>
                  ))}
                  <Typography variant="caption" sx={{ color: '#888', mt: 0.5, fontStyle: 'italic' }}>
                    AI:n läser dessa automatiskt vid generering.
                  </Typography>
                </Box>
              )}
            </Paper>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LinkedInIcon sx={{ color: '#0a66c2', fontSize: '1.1rem' }} />
              Förhandsgranskning
            </Typography>

            {isProduct ? (
              <Box>
                <Tabs value={tierTab} onChange={(_, v) => setTierTab(v)} sx={{ mb: 2, '& .MuiTab-root': { fontSize: '0.72rem', minHeight: 36 } }}>
                  {TIER_LABELS.map((l, i) => <Tab key={l} label={l} value={i} />)}
                </Tabs>
                {TIER_LABELS.map((_, i) => {
                  const selectedProduct = products.find(p => p.name === post.productName);
                  const productAvatar = selectedProduct?.imageUrl || null;
                  const tierAvatar = i === 0 ? productAvatar : i === 1 ? companyLogoUrl : null;
                  return (
                    <Box key={i} sx={{ display: tierTab === i ? 'block' : 'none' }}>
                      <LinkedInPreviewCard
                        authorName={i === 0 ? (post.tiers[0]?.authorName || post.productName || 'FLATBOX') : i === 1 ? 'Tikab Strukturmekanik AB' : (post.tiers[2]?.authorName || 'Medarbetarens namn')}
                        authorTitle={i === 0 ? (post.tiers[0]?.authorTitle || 'Innovativ produkt') : i === 1 ? 'Konsultföretag inom teknik • Solna' : (post.tiers[2]?.authorTitle || 'Titel • Tikab')}
                        authorAvatar={tierAvatar}
                        content={post.tiers[i]?.content || ''}
                        images={post.tiers[i]?.imageUrls || []}
                        likeCount={post.likes.length}
                        isLiked={isLiked}
                        onLike={!isNew ? handleLike : null}
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <LinkedInPreviewCard
                authorName={post.type === 'ambassadör' ? (post.assignedToName || 'Medarbetarens namn') : 'Tikab Strukturmekanik AB'}
                authorTitle={post.type === 'ambassadör' ? 'Medarbetare på Tikab Strukturmekanik AB' : 'Konsultföretag inom teknik • Solna'}
                authorAvatar={post.type === 'ambassadör' ? null : companyLogoUrl}
                content={post.content || ''}
                images={post.imageUrls}
                likeCount={post.likes.length}
                isLiked={isLiked}
                onLike={!isNew ? handleLike : null}
              />
            )}

            {/* Kopiera-knapp */}
            {(isProduct ? post.tiers[tierTab]?.content : post.content) && (
              <Button
                size="small" startIcon={<ContentCopyIcon />}
                onClick={() => {
                  const text = isProduct ? post.tiers[tierTab]?.content : post.content;
                  navigator.clipboard.writeText(text);
                  setSnack('Kopierat till urklipp!');
                }}
                sx={{ mt: 1.5, width: '100%' }}
                variant="outlined"
              >
                Kopiera inläggstext
              </Button>
            )}

            {/* ── Statistik ── */}
            <Paper sx={{ mt: 2.5, p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <BarChartIcon sx={{ color: '#0a66c2', fontSize: '1.1rem' }} />
                <Typography variant="subtitle2" fontWeight={700}>Statistik (manuell)</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Logga nyckeltal från LinkedIn efter publicering.
              </Typography>
              <TextField
                label="Länk till LinkedIn-inlägget"
                value={post.linkedInUrl}
                onChange={e => setField('linkedInUrl', e.target.value)}
                size="small" fullWidth
                placeholder="https://www.linkedin.com/posts/..."
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: post.linkedInUrl && (
                    <Tooltip title="Öppna inlägget">
                      <IconButton size="small" onClick={() => window.open(post.linkedInUrl, '_blank')}>
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              {[
                { key: 'visningar',   label: 'Visningar',   icon: <VisibilityIcon sx={{ fontSize: '1rem', color: '#1565C0' }} /> },
                { key: 'reaktioner',  label: 'Reaktioner',  icon: <FavoriteIcon   sx={{ fontSize: '1rem', color: '#C62828' }} /> },
                { key: 'kommentarer', label: 'Kommentarer', icon: <ChatIcon       sx={{ fontSize: '1rem', color: '#2E7D32' }} /> },
                { key: 'delningar',   label: 'Delningar',   icon: <ShareOutlinedIcon sx={{ fontSize: '1rem', color: '#E65100' }} /> },
                { key: 'klick',       label: 'Klick',       icon: <OpenInNewIcon  sx={{ fontSize: '1rem', color: '#6A1B9A' }} /> },
              ].map(({ key, label, icon }) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {icon}
                  <TextField
                    label={label} value={post.stats?.[key] || ''}
                    onChange={e => setField('stats', { ...post.stats, [key]: e.target.value })}
                    size="small" type="number" inputProps={{ min: 0 }} sx={{ flex: 1 }}
                  />
                </Box>
              ))}
              {post.stats?.visningar > 0 && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Engagemang:{' '}
                    <strong>
                      {(((Number(post.stats.reaktioner || 0) + Number(post.stats.kommentarer || 0) + Number(post.stats.delningar || 0)) / Number(post.stats.visningar)) * 100).toFixed(1)}%
                    </strong>
                    {' '}(reaktioner + kommentarer + delningar / visningar)
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Container>

      {/* Dialog: Begär ändringar */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GppMaybeIcon sx={{ color: '#E65100' }} />
          Begär ändringar
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Beskriv vad som behöver justeras. Inlägget skickas tillbaka till Utkast med din kommentar synlig för skaparen.
          </Typography>
          <TextField
            label="Kommentar" value={reviewComment}
            onChange={e => setReviewComment(e.target.value)}
            multiline minRows={3} fullWidth autoFocus
            placeholder="T.ex. 'Tonen behöver vara mer passionerad, och vi saknar ett strategiskt perspektiv i inledningen...'"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReviewDialog(false)}>Avbryt</Button>
          <Button
            variant="contained" onClick={handleRequestChanges}
            disabled={!reviewComment.trim() || saving}
            sx={{ bgcolor: '#E65100', '&:hover': { bgcolor: '#bf360c' } }}
          >
            Skicka tillbaka
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Radera inlägg */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteOutlineIcon sx={{ color: '#C62828' }} />
          Radera inlägg?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Inlägget <strong>"{post.title || 'Namnlöst inlägg'}"</strong> tas bort permanent och kan inte återställas.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(false)}>Avbryt</Button>
          <Button
            variant="contained" color="error"
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
            onClick={handleDelete}
            disabled={deleting}
          >
            Radera
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Skapa ny produkt */}
      <Dialog open={productDialog} onClose={() => setProductDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddCircleOutlineIcon sx={{ color: '#FF6B00' }} />
          Ny produkt
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ange produktens namn så att den visas i listan framöver.
          </Typography>
          <TextField
            label="Produktnamn (t.ex. FLATBOX)"
            value={newProductName}
            onChange={e => setNewProductName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddProduct(); }}
            fullWidth autoFocus
            placeholder="FLATBOX, StructScan, …"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProductDialog(false)}>Avbryt</Button>
          <Button
            variant="contained"
            startIcon={productSaving ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon />}
            onClick={handleAddProduct}
            disabled={!newProductName.trim() || productSaving}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}
          >
            Skapa
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
